import * as Sentry from '@sentry/react';
import { lazy, type ReactNode, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import RequireAuth from 'components/RequireAuth/RequireAuth';
import MyCompanyDetailsPage from 'src/pages/Protected/MyAccount/MyCompanyDetailsPage/MyCompanyDetailsPage';

import { appName, appVersion } from '../config/buildInfo';
import { environment } from '../constants/environment';
import { AppRoutes } from './AppRoutes';
import { PasswordType } from './Auth/PasswordPage/PasswordType.enum';

// Lazy load routes
const NotFoundPage = lazy(() => import('./Common/NotFoundPage/NotFoundPage'));
const ErrorPage = lazy(() => import('./Common/ErrorPage/ErrorPage'));
const SignInPage = lazy(() => import('./Auth/SignInPage/SignInPage'));
const ForgotPasswordPage = lazy(() => import('./Auth/ForgotPasswordPage/ForgotPasswordPage'));
const SignOutPage = lazy(() => import('./Auth/SignOutPage/SignOutPage'));
const PasswordPage = lazy(() => import('./Auth/PasswordPage/PasswordPage'));

const MyAccountDashboard = lazy(() => import('./Protected/MyAccount/MyAccountDashboard/MyAccountDashboard'));
const MyProfilePage = lazy(() => import('./Protected/MyAccount/MyProfilePage/MyProfilePage'));
const MyProjectsPage = lazy(() => import('./Protected/Folders/MyProjectsPage/MyProjectsPage'));
const AdminTabs = lazy(() => import('./Protected/Admin/AdminTabs/AdminTabs'));
const AdminDetails = lazy(() => import('./Protected/Admin/AdminDetails/AdminDetails'));
const AdminCompanyDetailsPage = lazy(() => import('./Protected/Admin/AdminCompanyDetailsPage/AdminCompanyDetailsPage'));
const AdminCompanies = lazy(() => import('./Protected/Admin/AdminCompanies/AdminCompanies'));

const HomeLayout = lazy(() => import('./Protected/HomeLayout'));
const AuthLayout = lazy(() => import('./Auth/AuthLayout'));

const PPAGeneralInfoPage = lazy(() => import('./Protected/PPA/PPAGeneralInfoPage/PPAGeneralInfoPage'));
const PPALayout = lazy(() => import('./Protected/PPA/PPALayout/PPALayout'));
const CGUBalanceSheetPage = lazy(
  () => import('./Protected/PPA/CGUBalanceSheetPages/CGUBalanceSheetPage/CGUBalanceSheetPage'),
);
const SumBalanceSheetPage = lazy(
  () => import('./Protected/PPA/CGUBalanceSheetPages/SumBalanceSheetPage/SumBalanceSheetPage'),
);
const ConsiderationPage = lazy(
  () => import('./Protected/PPA/ConsiderationPages/ConsiderationSetupPage/ConsiderationSetupPage'),
);
const EarnOutComponentPage = lazy(
  () => import('./Protected/PPA/ConsiderationPages/EarnOutComponentPage/EarnOutComponentPage'),
);
const DeferredPaymentPage = lazy(
  () => import('./Protected/PPA/ConsiderationPages/DeferredPaymentPage/DeferredPaymentPage'),
);
const ConsiderationOverviewPage = lazy(
  () => import('./Protected/PPA/ConsiderationPages/ConsiderationOverviewPage/ConsiderationOverviewPage'),
);

const DCFTabs = lazy(() => import('./Protected/PPA/DCFPages/components/DCFTabs'));
const DCFGeneralInputPage = lazy(() => import('./Protected/PPA/DCFPages/DCFPage/DCFGeneralInputPage'));
const DCFProjectionsInputPage = lazy(
  () => import('./Protected/PPA/DCFPages/DCFProjectionsInputPage/DCFProjectionsInputPage'),
);
const DCFResultsPage = lazy(() => import('./Protected/PPA/DCFPages/DCFResultsPage/DCFResultsPage'));

const IntangibleAssetsPage = lazy(
  () => import('./Protected/PPA/IntangibleAssetsPages/IntangibleAssetsPage/IntangibleAssetsPage'),
);
const IntangibleAssetTabs = lazy(() => import('./Protected/PPA/IntangibleAssetsPages/components/IntangibleAssetTabs'));
const IntangibleAssetPage = lazy(
  () => import('./Protected/PPA/IntangibleAssetsPages/IntangibleAssetPage/IntangibleAssetPage'),
);
const IntangibleAssetGeneralInputPageRFR = lazy(
  () => import('./Protected/PPA/IntangibleAssetsPages/IntangibleAssetGeneralInputPage/IntangibleAssetGeneralInputPage'),
);
const IntangibleAssetResultsPage = lazy(
  () => import('./Protected/PPA/IntangibleAssetsPages/IntangibleAssetResultsPage/IntangibleAssetResultsPage'),
);
const IntangibleAssetProjectionsInputPage = lazy(
  () =>
    import(
      './Protected/PPA/IntangibleAssetsPages/IntangibleAssetProjectionsInputPage/IntangibleAssetProjectionsInputPage'
    ),
);
const WorkforceValuationPage = lazy(
  () => import('./Protected/PPA/IntangibleAssetsPages/WorkforceValuation/WorkforceValuationPage'),
);
const FairValueBalanceSheetPage = lazy(
  () => import('./Protected/PPA/FairValueBalanceSheetPage/FairValueBalanceSheetPage'),
);
const IRRWARAPage = lazy(() => import('./Protected/PPA/IRRWARAPage/IRRWARAPage'));

const PPAResultPage = lazy(() => import('./Protected/PPA/PPAResultPage/PPAResultPage'));

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.01,
  release: `${appName}@${appVersion}`,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    // Check if it is an exception, and if so, show the report dialog
    // Source: https://docs.sentry.io/platforms/javascript/enriching-events/user-feedback/
    if (event.exception) {
      Sentry.showReportDialog({ eventId: event.event_id, lang: 'en' });
    }
    return event;
  },
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const RequireAuthRoute = (children: ReactNode, isPublic = false, preventIsPublicToast = false) => (
  <RequireAuth isPublic={isPublic} preventIsPublicToast={preventIsPublicToast}>
    {children}
  </RequireAuth>
);

// TODO: place errorElement in main Outlet
const router = sentryCreateBrowserRouter(
  [
    {
      element: <AuthLayout />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: AppRoutes.SignIn,
          element: RequireAuthRoute(<SignInPage />, true, true),
        },
        {
          path: AppRoutes.ForgotPassword,
          element: RequireAuthRoute(<ForgotPasswordPage />, true, true),
        },
        {
          path: AppRoutes.SignOut,
          element: RequireAuthRoute(<SignOutPage />),
        },
        {
          path: AppRoutes.ResetPassword,
          element: RequireAuthRoute(<PasswordPage variant={PasswordType.RESET} />, true, true),
        },
        {
          path: AppRoutes.RegisterInvite,
          element: RequireAuthRoute(<PasswordPage variant={PasswordType.ACTIVATE} />, true, true),
          errorElement: <ErrorPage />,
        },
      ],
    },
    {
      path: AppRoutes.Home,
      element: RequireAuthRoute(<HomeLayout />),
      errorElement: <ErrorPage />,
      children: [
        {
          path: AppRoutes.Home,
          element: <MyProjectsPage />,
        },
        // My Account Routes
        {
          path: AppRoutes.MyProfile,
          element: (
            <MyAccountDashboard>
              <MyProfilePage />
            </MyAccountDashboard>
          ),
        },
        // Company routes
        {
          path: AppRoutes.MyCompanyDetails,
          element: (
            <MyAccountDashboard>
              <MyCompanyDetailsPage />
            </MyAccountDashboard>
          ),
        },
        {
          /* Admin routes */
        },
        {
          path: AppRoutes.Admin,
          element: <AdminTabs />,
          children: [
            {
              path: AppRoutes.AdminCompanies,
              element: <AdminCompanies />,
            },
          ],
        },
        {
          path: AppRoutes.AdminCompanyDetails,
          element: (
            <AdminDetails>
              <AdminCompanyDetailsPage />
            </AdminDetails>
          ),
        },
      ],
    },
    // PPA routes
    {
      path: AppRoutes.PPA,
      element: <Navigate to={AppRoutes.Home} />,
    },
    {
      element: RequireAuthRoute(<PPALayout />),
      errorElement: <ErrorPage />,
      children: [
        {
          path: AppRoutes.PPADetails,
          element: <PPAGeneralInfoPage />,
        },
        {
          path: AppRoutes.ppaCguID,
          element: <CGUBalanceSheetPage />,
        },
        {
          path: AppRoutes.ppaCguOverview,
          element: <SumBalanceSheetPage />,
        },
        {
          path: AppRoutes.ppaConsideration,
          element: <ConsiderationPage />,
        },
        {
          path: AppRoutes.ppaConsiderationEarnOut,
          element: <EarnOutComponentPage />,
        },
        {
          path: AppRoutes.ppaConsiderationDeferredPayment,
          element: <DeferredPaymentPage />,
        },
        {
          path: AppRoutes.ppaConsiderationOverview,
          element: <ConsiderationOverviewPage />,
        },
        {
          path: AppRoutes.ppaDcf,
          element: <DCFTabs />,
          children: [
            {
              path: AppRoutes.ppaDcfForm,
              element: <DCFGeneralInputPage />,
            },
            {
              path: AppRoutes.ppaDcfInput,
              element: <DCFProjectionsInputPage />,
            },
            {
              path: AppRoutes.ppaDcfResults,
              element: <DCFResultsPage />,
            },
          ],
        },
        {
          path: AppRoutes.ppaIntangibleAssets,
          element: <IntangibleAssetsPage />,
        },
        {
          path: AppRoutes.ppaIntangibleAsset,
          element: <IntangibleAssetTabs />,
          children: [
            {
              path: AppRoutes.ppaIntangibleAsset,
              element: <IntangibleAssetPage />,
            },
            {
              path: AppRoutes.ppaIntangibleAssetGeneral,
              element: <IntangibleAssetGeneralInputPageRFR />,
            },
            {
              path: AppRoutes.ppaIntangibleAssetWorkforceValuation,
              element: <WorkforceValuationPage />,
            },
            {
              path: AppRoutes.ppaIntangibleAssetInput,
              element: <IntangibleAssetProjectionsInputPage />,
            },
            {
              path: AppRoutes.ppaIntangibleAssetResults,
              element: <IntangibleAssetResultsPage />,
            },
          ],
        },
        {
          path: AppRoutes.ppaFairValue,
          element: <FairValueBalanceSheetPage />,
        },
        {
          path: AppRoutes.ppaIrrWara,
          element: <IRRWARAPage />,
        },
        {
          path: AppRoutes.ppaReport,
          element: <PPAResultPage />,
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
    {
      path: AppRoutes.Error,
      element: <ErrorPage />,
    },
  ],
  {
    future: { v7_startTransition: true, v7_fetcherPersist: true, v7_relativeSplatPath: true },
  },
);

export default router;
